"use client";

import Image from "next/image";
import { usePathname } from "next/navigation";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import { resolveBg } from "@/lib/bg-color";
import { regionFromPathname, regionPath } from "@/lib/region";

type Asset = { filename?: string; alt?: string };

type FaqCalloutBadge = SbBlokData & {
  text?: string;
  color?: "purple" | "green";
};

type FaqCalloutBlok = SbBlokData & {
  title?: string;
  description?: string;
  button_label?: string;
  image?: Asset;
  badges?: FaqCalloutBadge[];
  bg_color?: unknown;
  reverse_order?: boolean;
};

const BADGE_STYLES: Record<string, string> = {
  purple: "bg-[#e0bff0] text-purple-05",
  green: "bg-[#dcfce7] text-[#166534]",
};

function ArrowIcon() {
  return (
    <svg
      width="17"
      height="11"
      viewBox="0 0 17 11"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M1 5.5h14.5M11 1l4.5 4.5L11 10"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function badgeClass(index: number): string {
  // Badges anchor to the image edges (wrapper is sized to the image).
  // Per Figma desktop (407:421): badge 0 juts past the right edge,
  // badge 1 hangs below the bottom, inset from the left.
  if (index === 0) {
    return "absolute top-5 -right-3 max-w-[120px] text-center lg:top-[47px] lg:right-[-47px] lg:max-w-none lg:w-[130px]";
  }
  if (index === 1) {
    return "absolute -bottom-3 left-1/2 -translate-x-1/2 whitespace-nowrap lg:bottom-[-29px] lg:left-[22px] lg:translate-x-0";
  }
  return "hidden";
}

export default function FaqCallout({ blok }: { blok: FaqCalloutBlok }) {
  const pathname = usePathname();
  const region = regionFromPathname(pathname);
  const faqsHref = regionPath("/faqs", region);
  const buttonLabel = blok.button_label || "Browse all FAQs";
  const badges = blok.badges || [];
  const { bg } = resolveBg(blok.bg_color, "white");
  const imageOrder = blok.reverse_order ? "md:order-1" : "md:order-2";
  const textOrder = blok.reverse_order ? "md:order-2" : "md:order-1";

  return (
    <section {...storyblokEditable(blok)} className={bg}>
      <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] pt-16 pb-[68px] lg:py-[54px]">
        <div className="flex flex-col gap-[38px] md:flex-row md:items-center md:gap-12 xl:justify-end xl:gap-[156px]">
          {blok.image?.filename && (
            <div className={`relative order-first w-full md:flex-1 md:shrink-0 xl:h-[368px] xl:w-[492px] xl:flex-initial ${imageOrder}`}>
              {/* Wrapper sized to the image. At xl it's absolute, flush-right
                  in the 492×368 frame so badges reach the section edge. */}
              <div className="relative mx-auto w-full max-w-[440px] xl:absolute xl:top-0 xl:right-[54px] xl:h-[338px] xl:max-w-none xl:w-[386px]">
                <div className="relative aspect-304/266 overflow-hidden rounded-[18px] xl:aspect-auto xl:h-full xl:rounded-[24px]">
                  <Image
                    src={blok.image.filename}
                    alt={blok.image.alt || ""}
                    fill
                    sizes="(min-width: 1280px) 386px, (min-width: 768px) 50vw, 100vw"
                    className="object-cover"
                  />
                </div>
                {badges.map((badge, i) => (
                  <div
                    key={badge._uid as string}
                    {...storyblokEditable(badge)}
                    className={`${badgeClass(i)} rounded-[7px] px-3 py-2 text-[15px] font-bold leading-tight shadow-sm xl:rounded-[8px] xl:px-3.5 xl:py-2.5 xl:text-body ${BADGE_STYLES[badge.color || "purple"]}`}
                  >
                    {badge.text}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={`flex flex-col gap-8 md:flex-1 md:shrink-0 xl:w-[510px] xl:flex-initial xl:py-2.5 ${textOrder}`}>
            <div className="flex flex-col gap-3 text-purple-05 lg:gap-5">
              {blok.title && (
                <h2 className="text-[clamp(2.25rem,4vw,3rem)] font-bold leading-[1.08]">
                  {blok.title}
                </h2>
              )}
              {blok.description && (
                <p className="text-body leading-[1.56]">{blok.description}</p>
              )}
            </div>
            <a
              href={faqsHref}
              className="inline-flex w-fit items-center gap-[7px] py-2.5 text-body font-extrabold leading-[1.054] text-purple-04 hover:underline"
            >
              {buttonLabel}
              <ArrowIcon />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
